import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Mail } from 'lucide-react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { FormExtrasContext } from '@/components/shared/crud-page';

interface AuthorizedSender { id: string; email: string }

/** Placeholders mirror the Zoho IoT_Suppliers form help text — the back-end
 *  email-send service substitutes these tokens before dispatch. Click a chip
 *  to copy it to the clipboard. */
const PLACEHOLDERS: Array<{ token: string; description: string }> = [
  { token: '[Fleet No]',           description: 'Asset fleet number' },
  { token: '[Make]',               description: 'Manufacturer' },
  { token: '[Model]',              description: 'Model name' },
  { token: '[Year]',               description: 'Year of manufacture' },
  { token: '[Color]',              description: 'Asset colour' },
  { token: '[Rego]',               description: 'Registration plate' },
  { token: '[VIN]',                description: 'Chassis / VIN' },
  { token: '[Engine No]',          description: 'Engine number' },
  { token: '[Serial No]',          description: 'Asset serial number' },
  { token: '[Provider Name]',      description: 'IoT supplier name' },
  { token: '[Purchase Order No]',  description: 'PO number on the asset request' },
  { token: '[IoT Install Location]', description: 'Onboarding location' },
];

function PlaceholderChip({ token }: { token: string }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs font-mono hover:bg-muted hover:border-primary/40"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(token);
          toast(`Copied ${token}`, undefined, 'success');
        } catch {
          toast('Copy failed', 'Clipboard not available', 'error');
        }
      }}
      title="Click to copy"
    >
      <Copy className="h-3 w-3" /> {token}
    </button>
  );
}

interface TemplateEditorProps {
  title: string;
  subtitle?: string;
  basePath: 'onboardTemplate' | 'offboardTemplate';
  values: Record<string, any>;
  onChange: (path: string, value: unknown) => void;
  fromOptions: AuthorizedSender[];
}

function TemplateEditor({ title, subtitle, basePath, values, onChange, fromOptions }: TemplateEditorProps) {
  const t = (values?.[basePath] ?? {}) as Record<string, any>;
  // `from` is now an email string (authorized sender). Empty/null →
  // no selection; the email service falls back to env.EMAIL_FROM.
  const fromValue: string | null =
    typeof t.from === 'string' && t.from.trim().length > 0 ? t.from.trim().toLowerCase() : null;
  // If a saved value isn't in the current authorized list (e.g. an entry
  // was removed after this template was saved), still render it as a
  // "stale" option so the user can see what's there and reassign.
  const isStale = fromValue !== null && !fromOptions.some((o) => o.email.toLowerCase() === fromValue);

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3 flex-row items-center gap-2 space-y-0">
        <Mail className="h-4 w-4 text-primary" />
        <div className="flex-1">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {/* `Enabled` checkbox retired — having one template per provider per
            direction made the explicit toggle redundant friction. Sends now
            fire whenever Recipients is non-empty; clear Recipients to pause. */}
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Select
              value={fromValue ?? '__none__'}
              onValueChange={(v) => onChange(`${basePath}.from`, v === '__none__' ? null : v)}
            >
              <SelectTrigger className="h-9"><SelectValue placeholder="— Select an authorized sender —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {/* Stale value (no longer in the authorized list) —
                    keep visible so the user can see+reassign rather
                    than have the field silently appear blank. */}
                {isStale && fromValue && (
                  <SelectItem value={fromValue}>{fromValue} (no longer authorized)</SelectItem>
                )}
                {fromOptions.map((e) => (
                  <SelectItem key={e.id} value={e.email}>{e.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Manage allowed senders under <span className="font-medium">Settings → Authorized Emails</span>.
            </p>
          </div>
          <div>
            <Label className="text-xs">Reply To</Label>
            <Input
              value={t.replyTo ?? ''}
              onChange={(e) => onChange(`${basePath}.replyTo`, e.target.value)}
              placeholder="reply@example.com"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Subject</Label>
          <Input
            value={t.subject ?? ''}
            onChange={(e) => onChange(`${basePath}.subject`, e.target.value)}
            placeholder="Asset onboarded — [Fleet No]"
          />
        </div>
        <div>
          <Label className="text-xs">Message</Label>
          <RichTextEditor
            value={t.message ?? ''}
            onChange={(html) => onChange(`${basePath}.message`, html)}
            placeholder="Hi [Provider Name],&#10;&#10;Please find the new asset details below…"
            minHeight="180px"
          />
        </div>
        <div>
          <Label className="text-xs">Recipients</Label>
          <Input
            value={t.recipients ?? ''}
            onChange={(e) => onChange(`${basePath}.recipients`, e.target.value)}
            placeholder="alice@example.com, bob@example.com"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Comma-separated list of recipient email addresses.</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * IoT Supplier email template editor — mirrors the Zoho `IoT_Suppliers` form.
 * Renders both the onboarding and offboarding email templates plus the
 * placeholder reference panel. Wired in via `CrudConfig.FormExtras`.
 */
export function IotEmailTemplatesSubform({ formValues, setFormValues }: FormExtrasContext) {
  // Source the From dropdown from /master-data/authorized-emails — the
  // curated list of approved sender addresses. Restricting to this list
  // keeps outgoing email aligned with whatever sender domain is set up
  // for SPF/DKIM, which is what stops messages from being flagged as
  // spam.
  const { data: fromOptions = [] } = useQuery<AuthorizedSender[]>({
    queryKey: ['/master-data/authorized-emails'],
    queryFn: async () => {
      const r = await api<any[]>('/master-data/authorized-emails?limit=500');
      const rows = (r.data || []) as any[];
      return rows
        .filter((e) => typeof e.email === 'string' && e.email.trim().length > 0)
        .map((e) => ({ id: e.id, email: e.email }));
    },
  });

  function setPath(path: string, value: unknown) {
    setFormValues((prev) => {
      const out = { ...prev };
      const parts = path.split('.');
      let cur: any = out;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] ?? {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return out;
    });
  }

  return (
    <div className="space-y-3 border-t pt-4">
      <div>
        <h3 className="text-sm font-semibold">Email Templates</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Notifications sent to this provider when an asset is onboarded or offboarded.
          Click a placeholder to copy it; the system substitutes these tokens at send time.
        </p>
      </div>

      <div className="rounded-md border bg-muted/20 p-3">
        <div className="text-xs font-medium text-muted-foreground mb-2">Available placeholders</div>
        <div className="flex flex-wrap gap-1.5">
          {PLACEHOLDERS.map((p) => (
            <PlaceholderChip key={p.token} token={p.token} />
          ))}
        </div>
      </div>

      <TemplateEditor
        title="Asset Onboard Notification"
        subtitle="Sent when a new asset is onboarded with this IoT supplier."
        basePath="onboardTemplate"
        values={formValues}
        onChange={setPath}
        fromOptions={fromOptions}
      />

      <TemplateEditor
        title="Asset Offboard Notification"
        subtitle="Sent when an asset is archived or disposed. Vehicle placeholders may not all apply."
        basePath="offboardTemplate"
        values={formValues}
        onChange={setPath}
        fromOptions={fromOptions}
      />
    </div>
  );
}
